import type { ShareFacts } from '@/modules/form/lang';

/**
 * Pronoun forms used by the news race sentence (#447), resolved from the
 * sender's stored preferred pronouns (e.g. "she/her").
 */
export interface PronounForms {
  /** Bare subject, e.g. "she". */
  subject: string;
  /** Subject + contracted copula, e.g. "she's" / "they're". */
  subjectIs: string;
  /** Possessive, e.g. "her" / "their". */
  possessive: string;
}

const KNOWN_PRONOUNS: Record<string, PronounForms> = {
  she: { subject: 'she', subjectIs: "she's", possessive: 'her' },
  he: { subject: 'he', subjectIs: "he's", possessive: 'his' },
  they: { subject: 'they', subjectIs: "they're", possessive: 'their' },
};

// The neutral fallback for unknown/custom pronoun sets: grammatical for any
// referent and never mis-genders.
const FALLBACK_FORMS: PronounForms = KNOWN_PRONOUNS.they;

export const ReshareStoryLogic = {
  /**
   * Resolve a profile's preferred-pronouns value ("she/her", "he/him",
   * "they/them", or free text) to the forms the race sentence needs. Custom
   * sets fall back to they/them: grammatical for anyone, never mis-gendering.
   */
  pronounForms(preferredPronouns: string | undefined): PronounForms {
    const subject = (preferredPronouns ?? '')
      .split('/')[0]
      .trim()
      .toLowerCase();
    return KNOWN_PRONOUNS[subject] ?? FALLBACK_FORMS;
  },

  /**
   * The impression + pronoun facts of the share frame, from the run's live
   * count and the sender's profile pronouns. These are general primitives
   * (#447) — any topic type can carry them. The goal itself is not here:
   * `_impressionGoal` is defined from the topic.
   */
  runFacts(
    runImpressionCount: number | undefined,
    preferredPronouns: string | undefined
  ): Pick<
    ShareFacts,
    | 'runImpressionCount'
    | 'preferredPronouns'
    | 'subjectPronoun'
    | 'subjectPronounIs'
    | 'possessivePronoun'
  > {
    const forms = ReshareStoryLogic.pronounForms(preferredPronouns);
    return {
      runImpressionCount,
      preferredPronouns: preferredPronouns?.trim() ?? '',
      subjectPronoun: forms.subject,
      subjectPronounIs: forms.subjectIs,
      possessivePronoun: forms.possessive,
    };
  },

  /**
   * A people count with its noun, singular-safe: "1 person" / "5 people".
   * Mirrors the `_runImpressionPeople` primitive (form/lang/primitive.ts must
   * stay free of share-module imports, so the one-liner lives in both).
   */
  peoplePhrase(count: number): string {
    return count === 1 ? '1 person' : `${count} people`;
  },

  /** Whether a run has reached the topic's effective impression goal (#447). */
  goalReached(
    runImpressionCount: number,
    impressionGoal: number | null | undefined
  ): boolean {
    return impressionGoal != null && runImpressionCount >= impressionGoal;
  },

  /**
   * Whether THIS sender's confirmed sends put the run over the goal — the
   * trigger for the last-confirm celebration (#447). runImpressionCount is
   * the final initiation response's total (it already includes this session's
   * sends); confirmedSendCount is the wizard's own send total.
   */
  crossedGoal(
    runImpressionCount: number,
    impressionGoal: number | null | undefined,
    confirmedSendCount: number
  ): boolean {
    return (
      impressionGoal != null &&
      runImpressionCount - confirmedSendCount < impressionGoal &&
      impressionGoal <= runImpressionCount
    );
  },
};
